import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getRoomLabelsTranslated} from 'components/Offer/hooks/useGetRoomLabels'
import {getOfferHasGhaOrGvrPreferredRate} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {getShouldShowYourChoiceTag} from 'modules/search/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {getCancellationDeadline} from '@vio/core/src/offer/business/cancellationPenalties'
import {
  getRoomLabels,
  roomLabels
} from '@vio/core/src/offer/business/getRoomLabels'

const TextContainer = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.--is-your-choice {
    flex-shrink: 0;
    max-width: 100%;
    margin-right: 2px;
    color: ${cssTheme.colors.content.success.secondary};
  }
`

const FreeCancellationDeadlineText: React.FC<{offer: SearchOffer}> = ({
  offer
}) => {
  const {formatDate, formatMessage} = useIntl()
  const freeCancellationDeadline = getCancellationDeadline(
    offer.cancellationPenalties
  )

  const date = formatDate(freeCancellationDeadline, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  })

  return (
    <>
      {formatMessage(roomLabels.freeCancellationDeadline, {
        date
      })}
    </>
  )
}

interface Props {
  offer: SearchOffer
  translatedOfferAccessFlowLabel: string | null
}

export const RoomLabelsText: React.FC<Props> = ({
  offer,
  translatedOfferAccessFlowLabel
}) => {
  const {formatMessage} = useIntl()
  const shouldShowYourChoice = useSelector(getShouldShowYourChoiceTag(offer))
  const shouldShowFreeCancellationDate = useSelector(state =>
    getOfferHasGhaOrGvrPreferredRate(state, offer)
  )

  const labels = getRoomLabels({
    offer,
    shouldShowYourChoice,
    shouldShowFreeCancellationDate
  })

  const filteredLabels = labels.filter(
    label => label?.id !== roomLabels.freeCancellationDeadline.id
  )

  const translatedLabels = getRoomLabelsTranslated({
    labels: filteredLabels,
    formatMessage
  })

  const isSingleYourChoiceLabel =
    shouldShowYourChoice && translatedLabels.length === 1
  const fullText = translatedLabels.join(' \u00B7 ')
  const otherLabels = shouldShowYourChoice
    ? [...translatedLabels].splice(1, 1).join(' \u00B7 ')
    : translatedLabels.join(' \u00B7 ')

  return (
    <Box display="flex" flexDirection="column" gap="s100">
      {translatedLabels?.length > 0 && (
        <Box display="flex" alignItems="flex-start" title={fullText}>
          {shouldShowYourChoice && (
            <TextContainer
              dataId="RoomLabels"
              variant="bodyXS"
              className={cx('--is-your-choice')}
            >
              {translatedLabels[0]}
              {translatedLabels.length > 1 && ' \u00B7 '}
            </TextContainer>
          )}

          {!isSingleYourChoiceLabel && (
            <TextContainer
              colorPath="content.neutral.secondary"
              variant="bodyXS"
              dataId="RoomLabels"
            >
              {otherLabels}
            </TextContainer>
          )}
        </Box>
      )}
      {shouldShowFreeCancellationDate || translatedOfferAccessFlowLabel ? (
        <Text colorPath="content.neutral.secondary" variant="bodyXS">
          {shouldShowFreeCancellationDate && (
            <FreeCancellationDeadlineText offer={offer} />
          )}
          {translatedOfferAccessFlowLabel && (
            <>
              {' \u00B7 '}
              {translatedOfferAccessFlowLabel}
            </>
          )}
        </Text>
      ) : null}
    </Box>
  )
}
